import {useCallback, useEffect, RefObject} from 'react';
import {isEqual} from 'underscore';

import useScrollSync, {ScrollSyncDirection} from 'ui-library/hooks/useScrollSync';

export const RECALCULATE_STICKY_HEADER_POSITIONING_TIME = 1000;
export interface StickyHeaderHandlerProps {
  tableName?: Element | null;
  verticalScrollContainer?: Element | null;
  horizontalScrollContainer?: Element | null;
  stickyHeaderRef: RefObject<HTMLDivElement | null>;
  titleBarHeight: number;
  headerRowHeight: number;
  isEnabled: boolean;
  canShowStickyHeader: boolean;
  setCanShowStickyHeader: (track: boolean) => void;
  stickyHeaderPositioning?: StickyHeaderPositioning;
  setStickyHeaderPositioning: (positioning: StickyHeaderPositioning) => void;
}

export interface StickyHeaderPositioning {
  top: number;
  left: number;
  width: number;
  containerLeft?: number; // needed to place the container for the scrolling sticky content
  containerRight?: number; // needed to place the container for the scrolling sticky content
}

/** A logic component for TableEditable that handles all logic needed to display a Sticky Header. This is
 * separate from the original component for organizational purposes and to make use of hooks.
 *
 * HOW THE STICKY HEADER WORKS
 *
 * The Sticky Header is rendered as a separate additional table header row above the main table. It has fixed
 * positioning since the table already has a horizontal scroll container that prevents position: sticky from
 * working vertically. When visible, it also makes the positioning of the Formula Box and Title Bar absolute
 * so that they are also shown as the user scrolls.
 *
 * Scrolling past the table's formula box sets canShowStickyHeader to true, initiating tracking of the sticky
 * header on the JS side, then set to false when you scroll past the bottom of the table. The Sticky Header
 * will only actually be visible to the user while they are focused on the table.
 *
 * The Sticky Header has its own horizontal scroll container that must be kept in sync with the table's scroll
 * using the useScrollSync hook.
 *
 * The fixed positioning of the Sticky Header is calculated dynamically so that it can be rendered in all
 * contexts including both the notebook and modals. These values can sometimes change because of settings
 * like print margins, so we poll for changes with a 1s interval.
 *
 * The affixed table (the frozen columns of the table) that shows when you scroll horizontally will also have
 * position: sticky applied to the header row when the Sticky Header is visible to allow for both a frozen
 * column and header.
 *
 * The CellResizer for the table also does one special check for the Sticky Header, where if we are hovering
 * above the Sticky Header, it will raise the z-index to be above the sticky header so that resizing can be
 * done on the sticky header.
 */
export default function StickyHeaderHandler({
  tableName,
  verticalScrollContainer,
  horizontalScrollContainer,
  stickyHeaderRef,
  titleBarHeight,
  headerRowHeight,
  isEnabled,
  canShowStickyHeader,
  setCanShowStickyHeader,
  stickyHeaderPositioning,
  setStickyHeaderPositioning,
}: StickyHeaderHandlerProps): null {
  // sync the horizontal scrolling of the sticky header with the rest of the table
  useScrollSync(horizontalScrollContainer, stickyHeaderRef.current, ScrollSyncDirection.Horizontal);

  const updateStickyHeaderPositioning = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    const tableNameRect = tableName?.getBoundingClientRect();
    const verticalScrollContainerRect = verticalScrollContainer?.getBoundingClientRect();
    const horizontalScrollContainerRect = horizontalScrollContainer?.getBoundingClientRect();
    if (
      !verticalScrollContainerRect ||
      !horizontalScrollContainerRect ||
      !tableNameRect ||
      !horizontalScrollContainer
    ) {
      return;
    }
    const newStickyHeaderPositioning: StickyHeaderPositioning = {
      top: verticalScrollContainerRect.top + titleBarHeight,
      left: horizontalScrollContainerRect.left,
      width: horizontalScrollContainerRect.width,
      containerLeft: tableNameRect.left,
      containerRight: tableNameRect.right,
    };
    if (!isEqual(stickyHeaderPositioning, newStickyHeaderPositioning)) {
      setStickyHeaderPositioning(newStickyHeaderPositioning);
    }
  }, [
    isEnabled,
    tableName,
    verticalScrollContainer,
    horizontalScrollContainer,
    titleBarHeight,
    stickyHeaderPositioning,
    setStickyHeaderPositioning,
  ]);

  /**
   * This determines if the table should display the sticky header.
   * This could be implemented with IntersectionObserver in the future, but is lacking Safari support as of
   * writing this.
   */
  const updateStickyHeaderVisibility = useCallback(() => {
    const verticalScrollContainerRect = verticalScrollContainer?.getBoundingClientRect();
    const horizontalScrollContainerRect = horizontalScrollContainer?.getBoundingClientRect();
    if (verticalScrollContainerRect && horizontalScrollContainerRect) {
      /** have we scrolled past the table's header */
      const isScrolledPastHeader =
        horizontalScrollContainerRect.top - verticalScrollContainerRect.top - titleBarHeight < 0;
      /** have we scrolled past the table (not inlcuding the height of the sticky headers themselves) */
      const isScrolledPastTable =
        horizontalScrollContainerRect.bottom -
          verticalScrollContainerRect.top -
          titleBarHeight -
          headerRowHeight <
        0;

      const newCanShowStickyHeader = isScrolledPastHeader && !isScrolledPastTable;
      if (newCanShowStickyHeader === canShowStickyHeader) {
        return; // no change in state, nothing to do
      }
      setCanShowStickyHeader(newCanShowStickyHeader);
      // If showing the sticky header, update the positioning immediately so that it renders faster
      if (newCanShowStickyHeader) {
        updateStickyHeaderPositioning();
      }
    }
  }, [
    horizontalScrollContainer,
    verticalScrollContainer,
    titleBarHeight,
    headerRowHeight,
    canShowStickyHeader,
    setCanShowStickyHeader,
    updateStickyHeaderPositioning,
  ]);

  const onVerticalScroll: EventListener = useCallback(() => {
    if (isEnabled) {
      updateStickyHeaderVisibility();
    }
  }, [isEnabled, updateStickyHeaderVisibility]);

  useEffect(() => {
    // Add an interval that constantly looks at the positioning of the table to determine any changes
    const stickyHeaderPosCalcIntervalId = setInterval(() => {
      if (horizontalScrollContainer) {
        updateStickyHeaderPositioning();
      }
    }, RECALCULATE_STICKY_HEADER_POSITIONING_TIME);

    // Add a scroll listener that determines if the sticky header should be displayed
    if (verticalScrollContainer && verticalScrollContainer !== document.body) {
      verticalScrollContainer.addEventListener('scroll', onVerticalScroll);
    }
    window.addEventListener('resize', updateStickyHeaderPositioning); // also recalculate on window resize

    return () => {
      if (verticalScrollContainer) {
        verticalScrollContainer.removeEventListener('scroll', onVerticalScroll);
      }
      window.removeEventListener('resize', updateStickyHeaderPositioning); // clean up the listener
      clearInterval(stickyHeaderPosCalcIntervalId);
    };
  }, [horizontalScrollContainer, verticalScrollContainer, onVerticalScroll, updateStickyHeaderPositioning]);

  // when the sticky header appears, scroll it immediately to match the table
  useEffect(() => {
    if (canShowStickyHeader) {
      requestAnimationFrame(() => {
        if (stickyHeaderRef.current && horizontalScrollContainer?.scrollLeft != null) {
          stickyHeaderRef.current.scrollLeft = horizontalScrollContainer.scrollLeft;
        }
      });
    }
  }, [canShowStickyHeader, horizontalScrollContainer, stickyHeaderRef]);

  // When first enabling the sticky header, position and show it immediately
  useEffect(() => {
    if (isEnabled) {
      updateStickyHeaderPositioning();
      updateStickyHeaderVisibility();
    }
  }, [isEnabled, updateStickyHeaderPositioning, updateStickyHeaderVisibility]);

  return null;
}
